import { useTranslation } from "react-i18next";

import { computationOptions } from "#app/computationOptions";
import { colors } from "#app/theme/v1/colors";
import { useOptimizedMachineTimes } from "#modules/optimizations/view/useOptimizedMachineTimes";
import { useOptimizedMaterialCosts } from "#modules/optimizations/view/useOptimizedMaterialCosts";
import { getProcessStepSummary } from "#modules/quoteRequestLines/domain/getProcessStepsSummary";
import { ProcessStepsTable } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/ProcessStepsTable";
import { useAssembliesContext } from "#modules/quoteRequests/context/AssembliesContext";
import { Stack } from "#shared/view/components/Stack";
import { Title } from "#shared/view/components/Title";
import { computeChildrenQuoteRequestLineCosts, isProductProcessStep } from "shared";

import { CostCollapsable } from "./CostCollapsable";

export const DetailsPanelAssemblySummary = ({
  quoteRequestLineId,
}: {
  quoteRequestLineId: string;
}) => {
  const { t } = useTranslation();

  const { assemblies } = useAssembliesContext();

  const currentAssembly = assemblies.find(
    (assembly) => assembly.quoteRequestLine.id === quoteRequestLineId,
  );
  const optimizedMachineTimes = useOptimizedMachineTimes(
    currentAssembly?.quoteRequestLine.quoteRequestId,
  );
  const optimizedMaterialCosts = useOptimizedMaterialCosts(
    currentAssembly?.quoteRequestLine.quoteRequestId,
  );

  const { childrenProcessesCost, childrenMaterialCost, childrenProductsCost } =
    computeChildrenQuoteRequestLineCosts(
      {
        children: currentAssembly?.children || [],
        optimizedMachineTimes,
        optimizedMaterialCosts,
      },
      computationOptions,
    );

  if (!currentAssembly) {
    return null;
  }

  const processStepsSummaryData = getProcessStepSummary(
    currentAssembly.children,
    optimizedMachineTimes,
  );

  const addedValueProcessStepsSummaryData = processStepsSummaryData.filter(
    (ps) => !isProductProcessStep(ps),
  );

  return (
    <Stack gap="xs">
      <Title order={1} color={colors.gray[6]}>
        {t("assembly.summary.title")}
      </Title>

      <Stack gap="xs">
        <CostCollapsable title={t("quoteRequestLine.addedValueCost")} value={childrenProcessesCost}>
          <ProcessStepsTable
            showCompanyMachines={false}
            processSteps={addedValueProcessStepsSummaryData}
            readonly
          />
        </CostCollapsable>
        <CostCollapsable
          title={t("quoteRequestLine.materialCost")}
          value={childrenMaterialCost}
        ></CostCollapsable>
        <CostCollapsable
          title={t("quoteRequestLine.productCost")}
          value={childrenProductsCost}
        ></CostCollapsable>
      </Stack>
    </Stack>
  );
};
